import api from '@api';
import AudienceTooltip from '@components/common/AudienceTooltip';
import Chip from '@components/common/Chip';
import config from '@config';
import { useAudienceDetails } from '@hooks';
import AudienceIcon from '@icons/audience.svg?react';
import CalendarIcon from '@icons/calendar.svg?react';
import FolderIcon from '@icons/folder.svg?react';
import InfoIcon from '@icons/info.svg?react';
import LocationIcon from '@icons/location.svg?react';
import ShapesIcon from '@icons/shapes.svg?react';
import { useChatStore } from '@store/chat';
import { useShellStore } from '@store/shell';
import { SecondaryButton as TextButton } from '@ui/buttons';
import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import type { Audience, SourcesOverviewResponse } from 'types/schema';
import Styled from './SourceOverview.styled';

interface Props {
  data: SourcesOverviewResponse;
}

function SourceOverview({ data }: Props) {
  const chatId = useChatStore((state) => state.chat.id);
  const isSourceInfoDialogOpen = useShellStore(
    (state) => state.isSourceInfoDialogOpen
  );
  const setIsSourceInfoDialogOpen = useShellStore(
    (state) => state.setIsSourceInfoDialogOpen
  );
  const [shouldShowMore, setShouldShowMore] = useState({
    audiences: false,
    topics: false
  });
  const audienceDetails = useAudienceDetails(data.audiences);
  const audienceDetailsReady = Array.isArray(audienceDetails);
  const once = useRef(false);

  useEffect(() => {
    const { sessionIsNew } = useChatStore.getState();

    if (sessionIsNew && audienceDetailsReady && once.current === false) {
      const audienceSizes = audienceDetails.flatMap(({ sample }) =>
        sample ? [sample] : []
      );
      const audienceUniverses = audienceDetails.flatMap(({ universe }) =>
        universe ? [universe] : []
      );
      const audienceNames = audienceDetails.flatMap(({ title }) =>
        title ? [title] : []
      );
      const audienceIds = audienceDetails.flatMap(({ id }) => (id ? [id] : []));

      once.current = true;

      api.analytics.loadedThinkingOverview({
        ['chat_id']: chatId,
        ['audiences_ids']: audienceIds.length > 0 ? audienceIds : undefined,
        ['audiences_names']:
          audienceNames.length > 0 ? audienceNames : undefined,
        ['audience_sizes']:
          audienceSizes.length > 0 ? audienceSizes : undefined,
        ['audience_universes']:
          audienceUniverses.length > 0 ? audienceUniverses : undefined,
        ['dataset_details']: data.datasets.map(({ title }) => title),
        ['locations']: data['location_groups'].map(({ group }) => group),
        ['topics']: data.topics,
        ['waves']: data['timeframe_groups'].flatMap(({ details }) =>
          details.timeframes.map(({ id }) => id)
        ),
        ['attribute_codes']: data['attribute_codes'].map(
          (item) =>
            `${item['namespace_code']}.${item['question_code']}_${item['datapoint_code']}`
        ),
        ['namespaces']: data.namespaces,
        ['question_codes']: data['questions'].map(
          ({ ['question_key']: key }) =>
            `${key['namespace_code']}.${key['question_code']}`
        ),
        ['question_names']: data['questions'].map(
          ({ ['question_key']: key, ['question_name']: name }) =>
            `${key['namespace_code']}.${key['question_code']} - ${name}`
        ),
        ['nb_audiences']: data.audiences.length,
        ['nb_topics']: data.topics.length
      });
    }
  }, [audienceDetails, audienceDetailsReady, chatId, data]);

  const handleOpenInfo = useCallback(() => {
    // Show the appendix
    setIsSourceInfoDialogOpen(!isSourceInfoDialogOpen);
    // Track event
    api.analytics.viewedSourceInfoDialog();
  }, [isSourceInfoDialogOpen, setIsSourceInfoDialogOpen]);

  const getAudienceChips = useCallback((audiences: Audience[]) => {
    return audiences.map((item, index) => {
      const tooltip = item['is_outdated'] ? (
        <Styled.TooltipContent>
          Audience was edited after the below insights were generated
        </Styled.TooltipContent>
      ) : (
        <AudienceTooltip audience={{ ...item, base: undefined }} />
      );

      return (
        <Chip
          tooltip={item.id ? tooltip : null}
          key={`source-overview-audience-${item.id}-${index}`}>
          {item.title}
        </Chip>
      );
    });
  }, []);

  const handleViewMoreItems = useCallback(
    (itemType: 'audiences' | 'topics') => {
      setShouldShowMore((shouldShowMore) => ({
        ...shouldShowMore,
        [itemType]: true
      }));

      // Track event
      api.analytics.viewedMoreThinkingOverviewItems();
    },
    []
  );

  const renderAudiences = useCallback(() => {
    const sorted = [...(audienceDetails || [])].sort(
      ({ title: a }, { title: b }) => (a < b ? -1 : 1)
    );
    const { visibilitySize } = config.chat.sources.audiences;
    const visibleAudiences = sorted.slice(0, visibilitySize);
    const hiddenAudiences = sorted.slice(visibilitySize);

    return (
      <Fragment>
        {getAudienceChips(visibleAudiences)}
        {hiddenAudiences.length > 0 && !shouldShowMore.audiences ? (
          <Styled.SeeMoreButtonWrapper>
            <Styled.SeeMoreButton
              data-testid="data-overview-see-more-button"
              onClick={() => handleViewMoreItems('audiences')}>
              +{hiddenAudiences.length} more
            </Styled.SeeMoreButton>
          </Styled.SeeMoreButtonWrapper>
        ) : null}
        {shouldShowMore.audiences && getAudienceChips(hiddenAudiences)}
      </Fragment>
    );
  }, [
    audienceDetails,
    shouldShowMore.audiences,
    getAudienceChips,
    handleViewMoreItems
  ]);

  const renderTopics = useCallback(() => {
    if (!data.topics) {
      return null;
    }

    const topics = [...data.topics];
    const hiddenTopics = topics.splice(
      config.chat.sources.topics.visibilitySize
    );

    return (
      <Fragment>
        {topics.map((item) => (
          <Chip key={item}>{item}</Chip>
        ))}
        {hiddenTopics.length > 0 && !shouldShowMore.topics ? (
          <Styled.SeeMoreButtonWrapper>
            <Styled.SeeMoreButton
              data-testid="data-overview-see-more-button"
              onClick={() => handleViewMoreItems('topics')}>
              +{hiddenTopics.length} more
            </Styled.SeeMoreButton>
          </Styled.SeeMoreButtonWrapper>
        ) : (
          hiddenTopics.map((item) => <Chip key={item}>{item}</Chip>)
        )}
      </Fragment>
    );
  }, [data, shouldShowMore.topics, handleViewMoreItems]);

  if (!data) {
    return null;
  }

  return (
    <Styled.Wrapper>
      <Styled.Table role="table">
        <Styled.TableRow data-testid="data-overview-audiences" role="row">
          <Styled.TableHeader role="rowheader">
            <Styled.TableHeaderLabel>
              <AudienceIcon />
              <span>Audience:</span>
            </Styled.TableHeaderLabel>
          </Styled.TableHeader>
          <Styled.TableData role="cell">{renderAudiences()}</Styled.TableData>
        </Styled.TableRow>
        {data.topics?.length > 0 && (
          <Styled.TableRow data-testid="data-overview-topics" role="row">
            <Styled.TableHeader role="rowheader">
              <Styled.TableHeaderLabel>
                <ShapesIcon />
                <span>Topic:</span>
              </Styled.TableHeaderLabel>
            </Styled.TableHeader>
            <Styled.TableData role="cell">{renderTopics()}</Styled.TableData>
          </Styled.TableRow>
        )}
        <Styled.TableRow data-testid="data-overview-timeframe" role="row">
          <Styled.TableHeader role="rowheader">
            <Styled.TableHeaderLabel>
              <CalendarIcon />
              <span>Timeframe:</span>
            </Styled.TableHeaderLabel>
          </Styled.TableHeader>
          <Styled.TableData role="cell">
            {data['timeframe_groups']?.map((item) => (
              <Chip key={item.group}>{item.group}</Chip>
            ))}
          </Styled.TableData>
        </Styled.TableRow>
        <Styled.TableRow data-testid="data-overview-locations" role="row">
          <Styled.TableHeader role="rowheader">
            <Styled.TableHeaderLabel>
              <LocationIcon />
              <span>Location:</span>
            </Styled.TableHeaderLabel>
          </Styled.TableHeader>
          <Styled.TableData role="cell">
            {data['location_groups']?.map((item) => (
              <Chip
                tooltip={
                  <Styled.TooltipContent>
                    <p>Default locations:</p>
                    <b>
                      {item.details.locations
                        ?.map((location) => location.name)
                        .join(', ')}
                    </b>
                  </Styled.TooltipContent>
                }
                key={item.group}>
                {item.group}
              </Chip>
            ))}
          </Styled.TableData>
        </Styled.TableRow>
        <Styled.TableRow data-testid="data-overview-dataset" role="row">
          <Styled.TableHeader role="rowheader">
            <Styled.TableHeaderLabel>
              <FolderIcon />
              <span>Data set:</span>
            </Styled.TableHeaderLabel>
          </Styled.TableHeader>
          <Styled.TableData role="cell">
            {data['datasets']?.map((item) => (
              <Chip
                tooltip={
                  <>
                    <Styled.TooltipContent>
                      <p>{item.description}</p>
                    </Styled.TooltipContent>
                    <Styled.TooltipFooter>
                      <TextButton
                        className="button"
                        testId="data-overview-dataset-learn-more-button"
                        onClick={() => window.open(item.url, '_blank')}>
                        Learn more
                      </TextButton>
                    </Styled.TooltipFooter>
                  </>
                }
                key={item.id}>
                {item.title || 'Untitled'}
              </Chip>
            ))}
          </Styled.TableData>
        </Styled.TableRow>
      </Styled.Table>
      <TextButton
        className="info-button"
        startIcon={<InfoIcon />}
        testId="source-info-button"
        onClick={handleOpenInfo}>
        Find out more about our data
      </TextButton>
    </Styled.Wrapper>
  );
}

export default memo(SourceOverview);
